import { Check, ChevronDown, Lock } from "lucide-react";
import { useRef, useState } from "react";

interface CategoryDropdownProps {
  options: string[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  locked?: boolean;
  lockedMessage?: string;
  hasError?: boolean;
}

export function CategoryDropdown({
  options,
  value,
  onChange,
  placeholder = "Select…",
  locked,
  lockedMessage,
  hasError,
}: CategoryDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const filtered = search
    ? options.filter((o) => o.toLowerCase().includes(search.toLowerCase()))
    : options;

  function select(opt: string) {
    onChange(opt);
    setOpen(false);
    setSearch("");
  }

  if (locked) {
    return (
      <div
        className={`form-input${hasError ? " is-error" : ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: "var(--muted)",
          cursor: "not-allowed",
          background: "var(--neutral-bg)",
        }}
      >
        <Lock size={14} />
        <span style={{ fontSize: 13 }}>{lockedMessage ?? "Select a work order first"}</span>
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className={`form-input${hasError ? " is-error" : ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          textAlign: "left",
        }}
        onClick={() => setOpen((o) => !o)}
        onBlur={(e) => {
          if (!ref.current?.contains(e.relatedTarget as Node)) {
            setOpen(false);
          }
        }}
      >
        <span style={{ color: value ? "var(--navy)" : "var(--muted-2)" }}>
          {value || placeholder}
        </span>
        <ChevronDown size={16} style={{ color: "var(--muted)", flexShrink: 0 }} />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "white",
            border: "1px solid var(--border-strong)",
            borderRadius: "var(--radius-sm)",
            boxShadow: "var(--shadow-lg)",
            maxHeight: 260,
            overflow: "auto",
            zIndex: 20,
          }}
        >
          {options.length > 5 && (
            <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)" }}>
              <input
                type="text"
                className="form-input"
                style={{ padding: "6px 10px", fontSize: 13 }}
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
          {filtered.map((opt) => (
            <button
              key={opt}
              type="button"
              style={{
                width: "100%",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                fontSize: 13,
                color: "var(--navy)",
                borderBottom: "1px solid #EFF1F5",
                background: opt === value ? "var(--surface-alt)" : "white",
                border: "none",
                textAlign: "left",
              }}
              onMouseDown={() => select(opt)}
            >
              {opt}
              {opt === value && <Check size={14} style={{ color: "var(--green-primary)" }} />}
            </button>
          ))}
          {filtered.length === 0 && (
            <div
              style={{ padding: "14px", color: "var(--muted)", fontSize: 13, textAlign: "center" }}
            >
              No options match
            </div>
          )}
        </div>
      )}
    </div>
  );
}
